import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Eye, EyeOff, Loader2, AlertCircle, Smartphone, CheckSquare, Square } from 'lucide-react'
import api from '../../services/api'

// Debounce helper
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, isAuthenticated } = useAuth()
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Stati per suggerimento email
  const [hasRememberedDevice, setHasRememberedDevice] = useState(false)
  const [emailHint, setEmailHint] = useState(null)
  const [suggestedEmail, setSuggestedEmail] = useState(null)
  const [showSuggestion, setShowSuggestion] = useState(false)
  const [loadingSuggestion, setLoadingSuggestion] = useState(false)

  // Stato per checkbox "Ricorda dispositivo" (solo educatori)
  const [rememberDevice, setRememberDevice] = useState(false)

  // Debounce del valore email per non fare troppe richieste
  const debouncedEmail = useDebounce(formData.email, 300)

  // FIX: Se già autenticato, redirect a dashboard
  useEffect(() => {
    if (isAuthenticated) {
      console.log('[LoginPage] Already authenticated, redirecting to dashboard')
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Verifica se esiste un dispositivo attendibile al mount
  useEffect(() => {
    checkRememberedDevice()
  }, [])

  // Cerca suggerimento email quando l'utente digita
  useEffect(() => {
    if (debouncedEmail.length >= 2 && hasRememberedDevice) {
      fetchEmailSuggestion(debouncedEmail)
    } else {
      setSuggestedEmail(null)
      setShowSuggestion(false)
    }
  }, [debouncedEmail, hasRememberedDevice])

  // Verifica se c'è un dispositivo salvato
  const checkRememberedDevice = async () => {
    try {
      // Leggi token da localStorage (fallback cross-domain)
      const deviceToken = localStorage.getItem('diario_trusted_device')
      
      const response = await api.get('/auth/devices/has-remembered', {
        headers: deviceToken ? { 'X-Device-Token': deviceToken } : {}
      })
      
      if (response.data.success && response.data.data.hasRemembered) {
        setHasRememberedDevice(true)
        setEmailHint(response.data.data.emailHint)
        
        // NUOVO: Pre-compila email se disponibile
        if (response.data.data.suggestedEmail) {
          setSuggestedEmail(response.data.data.suggestedEmail)
          // Pre-compila il campo email direttamente
          setFormData(prev => ({ ...prev, email: response.data.data.suggestedEmail }))
        }
      }
    } catch (err) {
      // Silently fail - non bloccare il login
      console.debug('No remembered device found')
    }
  }

  // Richiedi suggerimento email
  const fetchEmailSuggestion = async (prefix) => {
    if (prefix.length < 2) return

    try {
      setLoadingSuggestion(true)
      
      // Leggi token da localStorage (fallback cross-domain)
      const deviceToken = localStorage.getItem('diario_trusted_device')
      
      const response = await api.post('/auth/devices/email-suggest', { 
        prefix,
        deviceToken // Passa il token nel body
      })
      
      if (response.data.success && response.data.data.found) {
        setSuggestedEmail(response.data.data.email)
        // Mostra suggerimento solo se è diverso da quello già digitato
        setShowSuggestion(response.data.data.email.toLowerCase() !== prefix.toLowerCase())
      } else {
        setSuggestedEmail(null)
        setShowSuggestion(false)
      }
    } catch (err) {
      // Silently fail
      setSuggestedEmail(null)
      setShowSuggestion(false)
    } finally {
      setLoadingSuggestion(false)
    }
  }

  // Applica il suggerimento email
  const applySuggestion = useCallback(() => {
    if (suggestedEmail) {
      setFormData(prev => ({ ...prev, email: suggestedEmail }))
      setSuggestedEmail(null)
      setShowSuggestion(false)
      // Focus sul campo password
      document.getElementById('password')?.focus()
    }
  }, [suggestedEmail])

  // Gestione tasto Tab/Enter nel campo email per accettare suggerimento
  const handleEmailKeyDown = (e) => {
    if (showSuggestion && suggestedEmail) {
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault()
        applySuggestion()
      } else if (e.key === 'Escape') {
        setShowSuggestion(false)
      }
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      console.log('[LoginPage] Starting login...')
      const result = await login(formData.email, formData.password)
      
      console.log('[LoginPage] Login result:', result)
      
      // =========================================
      // GESTIONE 2FA
      // =========================================
      if (result.requires2FA) {
        // Redirect alla pagina di verifica 2FA con i dati
        navigate('/verify-2fa', { 
          state: { 
            twoFactorData: result.data,
            rememberDevice: rememberDevice
          },
          replace: true 
        })
        return
      }
      // =========================================

      if (result.success) {
        console.log('[LoginPage] Login successful, redirecting...')

        // Cookie httpOnly impostati dal backend — salva solo flag non-auth
        if (rememberDevice) {
          localStorage.setItem('pendingTrustDevice', 'true')
        }

        // Redirect — checkAuth() caricherà lo state dal cookie
        window.location.href = '/dashboard'
        return
        
      } else {
        setError(result.error || result.message || 'Errore durante il login')
        setLoading(false)
      }
    } catch (err) {
      console.error('[LoginPage] Login error:', err)
      setError(err.response?.data?.message || 'Errore durante il login')
      setLoading(false)
    }
  }

  // Helper per ottenere nome browser
  const getBrowserName = () => {
    const ua = navigator.userAgent
    if (ua.includes('Chrome')) return 'Chrome'
    if (ua.includes('Firefox')) return 'Firefox'
    if (ua.includes('Safari')) return 'Safari'
    if (ua.includes('Edge')) return 'Edge'
    return 'Browser'
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600 dark:text-primary-400">
            📘 Il Diario di Bordo
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Gestione centri educativi
          </p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Accedi
          </h2>

          {/* Hint dispositivo salvato */}
          {hasRememberedDevice && (
            <div className="mb-6 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg flex items-center gap-3">
              <Smartphone className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-primary-700 dark:text-primary-300">
                  Bentornato!
                </p>
                <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                  {formData.email 
                    ? `Accesso come ${formData.email}` 
                    : emailHint 
                      ? `Inizia a digitare per ${emailHint}`
                      : 'Dispositivo riconosciuto'
                  }
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="relative">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onKeyDown={handleEmailKeyDown}
                required
                autoComplete="email"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="nome@esempio.it"
              />
              
              {/* Suggerimento email autocomplete */}
              {showSuggestion && suggestedEmail && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={applySuggestion}
                    className="w-full px-4 py-3 text-left hover:bg-primary-50 dark:hover:bg-primary-900/30 flex items-center justify-between group transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Smartphone className="h-4 w-4 text-primary-500" />
                      <span className="text-gray-900 dark:text-white">
                        {suggestedEmail}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 group-hover:text-primary-500">
                      Tab per selezionare
                    </span>
                  </button>
                </div>
              )}

              {/* Loading indicator per suggerimento */}
              {loadingSuggestion && formData.email.length >= 2 && (
                <div className="absolute right-3 top-9">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Checkbox "Ricorda questo dispositivo" - Solo per educatori
                Nota: Coordinatori e Responsabili hanno 2FA obbligatorio,
                quindi vedranno l'opzione nella pagina di verifica 2FA */}
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => setRememberDevice(!rememberDevice)}
                className="flex-shrink-0 mt-0.5"
              >
                {rememberDevice ? (
                  <CheckSquare className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                ) : (
                  <Square className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-500" />
                )}
              </button>
              <div 
                className="flex-1 cursor-pointer"
                onClick={() => setRememberDevice(!rememberDevice)}
              >
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Ricorda questo dispositivo
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Non ti verrà richiesta la verifica per 14 giorni
                </p>
              </div>
            </div>

            {/* Forgot password */}
            <div className="flex justify-end">
              <Link 
                to="/forgot-password" 
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                Password dimenticata?
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Accesso in corso...
                </>
              ) : (
                'Accedi'
              )}
            </button>
          </form>

          {/* Register link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Non hai un account?{' '}
              <Link to="/register" className="text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium">
                Registra il tuo centro
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
          © {new Date().getFullYear()} Il Diario di Bordo
        </p>
      </div>
    </div>
  )
}
